import React from "react";
import "@/i18n";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { ConsentProvider } from "@/context/ConsentContext";
import { Toaster } from "@/components/ui/sonner";
import { SUPPORTED_LOCALES } from "@/i18n";
import CookieConsent from "@/components/CookieConsent";
import AnalyticsHost from "@/components/AnalyticsHost";
import ErrorBoundary from "@/components/ErrorBoundary";
import CommerceGate from "@/components/CommerceGate";

// Admin
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminLayout from "@/components/AdminLayout";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Recovery from "@/pages/Recovery";
import RecoveryUse from "@/pages/RecoveryUse";
import TwoFactorSetup from "@/pages/TwoFactorSetup";
import SetupAccount from "@/pages/SetupAccount";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminUsers from "@/pages/AdminUsers";
import AdminAuditLog from "@/pages/AdminAuditLog";
import AdminPages from "@/pages/AdminPages";
import AdminPageEditor from "@/pages/AdminPageEditor";
import AdminProducts from "@/pages/AdminProducts";
import AdminProductEditor from "@/pages/AdminProductEditor";
import AdminGallery from "@/pages/AdminGallery";
import AdminTranslations from "@/pages/AdminTranslations";
import AdminSettings from "@/pages/AdminSettings";
import AdminMessages from "@/pages/AdminMessages";
import AdminInquiries from "@/pages/AdminInquiries";
import AdminOrders from "@/pages/AdminOrders";
import AdminEmailLog from "@/pages/AdminEmailLog";
import MaintenanceGate from "@/components/MaintenanceGate";

// Public
import PublicLayout from "@/components/PublicLayout";
import Home from "@/pages/public/Home";
import Story from "@/pages/public/Story";
import HowItsMade from "@/pages/public/HowItsMade";
import Models from "@/pages/public/Models";
import Restorations from "@/pages/public/Restorations";
import Gallery from "@/pages/public/Gallery";
import Contact from "@/pages/public/Contact";
import Terms from "@/pages/public/Terms";
import Service from "@/pages/public/Service";
import InstrumentPage from "@/pages/public/InstrumentPage";
import Configure from "@/pages/public/Configure";
import Cart from "@/pages/public/Cart";
import Checkout from "@/pages/public/Checkout";
import CheckoutSuccess from "@/pages/public/CheckoutSuccess";
import CheckoutCancel from "@/pages/public/CheckoutCancel";

// The 6 public-facing instrument sub-page slugs. Mandolina + Berda (A,G) [berda-ag]
// dropped from the lineup; legacy /berde-ga + /berde-ag both 301-redirect to /berde.
const INSTRUMENT_SLUGS = [
  "primovi", "bisernice", "bracevi", "bugarije", "cela", "berde",
];

function LocaleGuard({ children }) {
  const { locale } = useParams();
  if (!SUPPORTED_LOCALES.includes(locale)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

// Helper: legacy /mandoline (all locales) → /models redirect
function MandolineRedirect() {
  return <Navigate to="/models" replace />;
}

// Legacy /berde-ga → /berde (client-side 301-equivalent; archived variant.)
function BerdaGaRedirect() {
  return <Navigate to="/berde" replace />;
}

// Legacy /berde-ag → /berde (client-side 301-equivalent; archived variant.)
function BerdaAgRedirect() {
  return <Navigate to="/berde" replace />;
}

function App() {
  return (
    <ErrorBoundary>
    <HelmetProvider>
      <AuthProvider>
        <CartProvider>
          <ConsentProvider>
          <BrowserRouter>
          <AnalyticsHost />
          <Routes>
            {/* Admin auth flows */}
            <Route path="/admin/login" element={<Login />} />
            <Route path="/admin/forgot-password" element={<ForgotPassword />} />
            <Route path="/admin/reset-password" element={<ResetPassword />} />
            <Route path="/admin/recovery" element={<Recovery />} />
            <Route path="/admin/recovery/use" element={<RecoveryUse />} />
            <Route path="/admin/setup-account" element={<SetupAccount />} />
            <Route
              path="/admin/2fa-setup"
              element={
                <ProtectedRoute allowForce2faReset>
                  <TwoFactorSetup />
                </ProtectedRoute>
              }
            />

            {/* Admin CMS (protected) */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute adminOnly>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="pages" element={<AdminPages />} />
              <Route path="pages/:slug" element={<AdminPageEditor />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="products/:id" element={<AdminProductEditor />} />
              <Route path="gallery" element={<AdminGallery />} />
              <Route path="translations" element={<AdminTranslations />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="messages" element={<AdminMessages />} />
              <Route path="inquiries" element={<AdminInquiries />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="email-log" element={<AdminEmailLog />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="audit-log" element={<AdminAuditLog />} />
            </Route>

            {/* Locale-prefixed public routes — en/de/fr (HR has no prefix) */}
            <Route
              path="/:locale"
              element={
                <LocaleGuard>
                  <MaintenanceGate>
                    <PublicLayout />
                  </MaintenanceGate>
                </LocaleGuard>
              }
            >
              <Route index element={<Home />} />
              <Route path="story" element={<Story />} />
              <Route path="how-its-made" element={<HowItsMade />} />
              <Route path="models" element={<Models />} />
              <Route path="restorations" element={<Restorations />} />
              <Route path="gallery" element={<Gallery />} />
              <Route path="contact" element={<Contact />} />
              <Route path="service" element={<Service />} />
              <Route path="terms" element={<Terms />} />
              {/* Commerce routes — gated by commerce_mode_enabled */}
              <Route path="configure/:slug" element={<CommerceGate><Configure /></CommerceGate>} />
              <Route path="cart" element={<CommerceGate><Cart /></CommerceGate>} />
              <Route path="checkout" element={<CommerceGate><Checkout /></CommerceGate>} />
              {/* Success stays accessible with session_id even when commerce is off (Stripe in-flight). */}
              <Route path="checkout/success" element={<CommerceGate allowIfSessionId><CheckoutSuccess /></CommerceGate>} />
              <Route path="checkout/cancel" element={<CommerceGate><CheckoutCancel /></CommerceGate>} />
              {INSTRUMENT_SLUGS.map((slug) => (
                <Route key={slug} path={slug} element={<InstrumentPage />} />
              ))}
              {/* Legacy: redirect /mandoline → /models within the locale */}
              <Route path="mandoline" element={<MandolineRedirect />} />
              {/* Legacy: /berde-ga + /berde-ag → /berde (archived variants) */}
              <Route path="berde-ga" element={<BerdaGaRedirect />} />
              <Route path="berde-ag" element={<BerdaAgRedirect />} />
            </Route>

            {/* HR (default, no locale prefix) */}
            <Route path="/" element={<MaintenanceGate><PublicLayout /></MaintenanceGate>}>
              <Route index element={<Home />} />
              <Route path="story" element={<Story />} />
              <Route path="how-its-made" element={<HowItsMade />} />
              <Route path="models" element={<Models />} />
              <Route path="restorations" element={<Restorations />} />
              <Route path="gallery" element={<Gallery />} />
              <Route path="contact" element={<Contact />} />
              <Route path="service" element={<Service />} />
              <Route path="terms" element={<Terms />} />
              <Route path="configure/:slug" element={<CommerceGate><Configure /></CommerceGate>} />
              <Route path="cart" element={<CommerceGate><Cart /></CommerceGate>} />
              <Route path="checkout" element={<CommerceGate><Checkout /></CommerceGate>} />
              <Route path="checkout/success" element={<CommerceGate allowIfSessionId><CheckoutSuccess /></CommerceGate>} />
              <Route path="checkout/cancel" element={<CommerceGate><CheckoutCancel /></CommerceGate>} />
              {INSTRUMENT_SLUGS.map((slug) => (
                <Route key={slug} path={slug} element={<InstrumentPage />} />
              ))}
              <Route path="mandoline" element={<MandolineRedirect />} />
              <Route path="berde-ga" element={<BerdaGaRedirect />} />
              <Route path="berde-ag" element={<BerdaAgRedirect />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <CookieConsent />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
          </ConsentProvider>
        </CartProvider>
      </AuthProvider>
    </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
